import {
  AvailabilityDisplayMode,
  BookingVisibilityType,
  ResourceAllocationType,
  TierPricingType,
} from "../GeneratedGraphQL";
import {
  Service,
  ServiceCategory,
  ServiceTier,
} from "../AppDataProvider/AppDataProvider";

import { defaultOrganisationId } from "./organisation";
import { PaymentOption } from "../GeneratedGraphQL";

export const defaultServiceId = "000000000000000000000001";

export const defaultServiceCategory: ServiceCategory = {
  _id: "000000000000000000000059",
  name: "General",
  order: 1,
};

export const otherServiceCategory: ServiceCategory = {
  _id: "000000000000000000000060",
  name: "Other",
  order: 2,
};

export const noOrderCategory: ServiceCategory = {
  _id: "000000000000000000000061",
  name: "Legacy",
};

export const singleDurationPaidService: Service = {
  _id: defaultServiceId,
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the first test service. It has a single duration and price.",
  durations: [{ minutes: 30, oldPrice: 120, price: 100 }],
  image: "https://i.redd.it/vxj8b6wtl6481.jpg",
  name: "test service 1",
  numberOfResources: 3,
  occupancy: 20,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

/**
 * An example of a service with a duration greater than 1 day, in this case 2 days.
 */
export const extendedSingleDurationPaidService: Service = {
  ...singleDurationPaidService,
  durations: [{ minutes: 2880, oldPrice: 120, price: 100 }],
};

export const discountCodeEnabledService: Service = {
  _id: "000000000000000000000091",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the first test service. It has a single duration and price.",
  durations: [{ minutes: 60, price: 100 }],
  image: "https://i.redd.it/vxj8b6wtl6481.jpg",
  isDiscountCodeAvailable: true,
  name: "test service 1 with discount code",
  numberOfResources: 2,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const singleDurationPaidDepositService: Service = {
  _id: "000000000000000000000081",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "30",
  bookingPaymentOption: PaymentOption.Deposit,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the deposit service. It has a single duration and price.",
  durations: [{ minutes: 60, price: 100 }],
  image: "https://i.redd.it/vxj8b6wtl6481.jpg",
  name: "test service 1 (deposit)",
  numberOfResources: 3,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const singleDurationFreeService: Service = {
  _id: "000000000000000000000002",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description: "This is the second test service. It is free.",
  durations: [{ minutes: 60 }],
  image: "https://i.redd.it/zlrtqgsw0q251.jpg",
  name: "test service 2",
  numberOfResources: 5,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const multiDurationService: Service = {
  _id: "000000000000000000000003",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the third test service. It has multiple durations and prices",
  durations: [
    { default: true, minutes: 30, oldPrice: 70, price: 60 },
    { default: false, minutes: 60, oldPrice: 0, price: 100 },
    { default: false, minutes: 90, price: 120 },
  ],
  image: "https://i.redd.it/ufyz6dfbzxk81.jpg",
  name: "test service 3",
  numberOfResources: 3,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const poaService: Service = {
  _id: "0000000000000000000000022",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description: "This is a service that is set to be price of application",
  durations: [{ default: false, minutes: 90, POA: true, price: 0 }],
  image: "https://i.redd.it/ufyz6dfbzxk81.jpg",
  name: "test service 55",
  numberOfResources: 3,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const singleDurationNoPaymentService: Service = {
  _id: "0000000000000000000000011",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is a service with a duration that has a price, but payment is not enabled",
  durations: [{ minutes: 30, price: 10 }],
  image: "https://i.redd.it/zlrtqgsw0q251.jpg",
  name: "test service with price & payment disabled",
  numberOfResources: 3,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const multiplePricingTypeTier = {
  _id: "1",
  description: "This is the first tier.",
  name: "Tier 1",
  pricePoints: [
    {
      _id: "a",
      name: "First price point",
      pricePerQuantity: 10,
      spacesPerQuantity: 5,
    },
    {
      _id: "b",
      name: "Second price point",
      pricePerQuantity: 20,
      spacesPerQuantity: 10,
    },
    {
      _id: "c",
      name: "Third price point",
      pricePerQuantity: 0,
      spacesPerQuantity: 3,
    },
  ],
  pricingType: TierPricingType.Multiple,
};

export const singlePricingTypeTier = {
  _id: "2",
  description: "This is the second tier.",
  name: "Tier 2",
  pricePoints: [
    {
      _id: "d",
      name: "First price point",
      pricePerQuantity: 100,
      spacesPerQuantity: 10,
    },
  ],
  pricingType: TierPricingType.Single,
};

export const tiers: ServiceTier[] = [
  multiplePricingTypeTier,
  singlePricingTypeTier,
  {
    _id: "3",
    description: "This is the third tier.",
    name: "Tier 3",
    pricePoints: [
      {
        _id: "e",
        name: "First price point",
        pricePerQuantity: 0,
        spacesPerQuantity: 7,
      },
    ],
    pricingType: TierPricingType.Single,
  },
];

export const multiTierService: Service = {
  _id: "000000000000000000000004",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the fourth test service. It has multiple tiers/price points",
  durations: [{ minutes: 90 }],
  image: "https://i.redd.it/sjrsx4mhtgz81.jpg",
  maxParticipantsPerBooking: 30,
  name: "test service 4",
  numberOfResources: 2,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
  tiers,
};

export const multiTierServiceSingleResource: Service = {
  _id: "000000000000000000000004",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the fourth test service. It has multiple tiers/price points",
  durations: [{ minutes: 90 }],
  image: "https://i.redd.it/sjrsx4mhtgz81.jpg",
  maxParticipantsPerBooking: 30,
  name: "test service 4",
  numberOfResources: 1,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
  tiers,
};

export const multiDurationSinglePriceService: Service = {
  _id: "000000000000000000000005",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the fifth test service. It has multiple durations but they all cost the same",
  durations: [
    { minutes: 30, price: 2.5 },
    { default: true, minutes: 60, price: 2.5 },
    { minutes: 90, price: 2.5 },
  ],
  image: "https://i.redd.it/w40z4weuulu31.jpg",
  name: "test service 5",
  numberOfResources: 2,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const fiveParticipantsSingleDurationPaidService: Service = {
  _id: "000000000000000000000006",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the sixth test service. It allows up to five participants per booking and has a price.",
  durations: [{ minutes: 30, price: 2.5 }],
  image: "https://i.redd.it/fgpoyfgskb091.jpg",
  maxParticipantsPerBooking: 5,
  name: "test service 6",
  numberOfResources: 2,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const fiveParticipantsMultiDurationSinglePriceService: Service = {
  _id: "000000000000000000000007",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the seventh test service. It allows up to five participants per booking and has more than one duration.",
  durations: [
    { minutes: 30, price: 2.5 },
    { minutes: 60, price: 2.5 },
    { default: true, minutes: 90, price: 2.5 },
  ],
  image: "https://i.redd.it/6fx531gqr4o91.jpg",
  maxParticipantsPerBooking: 5,
  name: "test service 7",
  numberOfResources: 4,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const fiveParticipantsMultiDurationMultiPriceService: Service = {
  _id: "000000000000000000000008",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.Full,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the eighth test service. It allows up to five participants per booking and has more than one duration, and the durations each have a different price.",
  durations: [
    { minutes: 30, price: 1 },
    { minutes: 60, price: 2.5 },
    { minutes: 90, price: 5 },
    { minutes: 1 },
  ],
  image: "https://i.redd.it/5w6mwabsztp91.jpg",
  maxParticipantsPerBooking: 5,
  name: "test service 8",
  numberOfResources: 2,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const fiveParticipantsSingleDurationFreeService: Service = {
  _id: "000000000000000000000009",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description:
    "This is the ninth test service. It allows up to five participants per booking and is free.",
  durations: [{ minutes: 30 }],
  image: "https://pbs.twimg.com/media/Fo2tV7zXwAEFao0?format=jpg&name=medium",
  maxParticipantsPerBooking: 5,
  name: "test service 9",
  numberOfResources: 2,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
};

export const multiTierServiceWithNoTiers: Service = {
  _id: "000000000000000000000010",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  description: "This is the tenth test service. It has an empty tiers array.",
  durations: [{ minutes: 30 }],
  name: "test service 10",
  numberOfResources: 4,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
  tiers: [],
};

export const serviceNoDescription: Service = {
  _id: "000000000000000000000011",
  availabilityDisplayMode: AvailabilityDisplayMode.Calendar,
  bookingDepositAmount: "0",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Bookable,
  category: defaultServiceCategory,
  durations: [{ minutes: 30 }],
  image: "https://i.redd.it/sjrsx4mhtgz81.jpg",
  name: "test service 11",
  numberOfResources: 2,
  organisation: {
    _id: defaultOrganisationId,
  },
  resourceAllocationType: ResourceAllocationType.Any,
  tiers: [],
};

export const unavailableService = {
  ...singleDurationPaidService,
  _id: "000000000000000000000033",
  bookingVisibilityType: BookingVisibilityType.Unavailable,
  name: "Unavailable service",
};

export const requestOnlyService = {
  ...singleDurationPaidService,
  _id: "000000000000000000000034",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Request,
  durations: [{ minutes: 30 }],
  name: "Request only service",
};

export const otherService = {
  ...singleDurationPaidService,
  _id: "000000000000000000000035",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Request,
  category: otherServiceCategory,
  durations: [{ minutes: 30 }],
  name: "Other cat service",
};

export const serviceWithUnsafeDescription: Service = {
  ...singleDurationPaidService,
  _id: "000000000000000000000035",
  description: `<p>unsafe <a data-testid="unsafe" href="/" onmouseover="alert('mouseover');">thing</a></p>`,
};

export const legacyCategoryService = {
  ...singleDurationPaidService,
  _id: "000000000000000000000037",
  bookingPaymentOption: PaymentOption.None,
  bookingVisibilityType: BookingVisibilityType.Request,
  category: noOrderCategory,
  durations: [{ minutes: 30 }],
  name: "Service with an unordered category",
};

export const defaultService: Service = singleDurationPaidService;
export const multipleServices: Service[] = [
  singleDurationPaidService,
  singleDurationFreeService,
  multiDurationService,
  multiTierService,
  multiDurationSinglePriceService,
  multiTierServiceWithNoTiers,
  requestOnlyService,
  otherService,
];
export const complexDurationAndAttendeesServices: Service[] = [
  multiDurationSinglePriceService,
  multiDurationService,
  fiveParticipantsSingleDurationPaidService,
  fiveParticipantsSingleDurationFreeService,
  fiveParticipantsMultiDurationSinglePriceService,
  fiveParticipantsMultiDurationMultiPriceService,
];
